import { URLInitOptions } from './routing';
import { BLACKLISTED_SEARCH_PARAMETERS } from './router.config';

/**
 * Initialization function. It extracts the base pathname from the given `url`.
 * This is the whole URL from the end of `mecerdes-benz.??/` to the last ocurrence of `….html`.
 *
 * @param {URL} url - The URL from which to extract the href
 * @returns {*} {string} - The URL's pathname corresponding to the base pathname
 */
export function extractBasePathname(url: URL, delimiter: string): string {
  const htmlEndIndex = url.pathname.lastIndexOf(delimiter);
  if (htmlEndIndex >= 0) {
    // Ensuring that basePathNames always end with '/'.
    // Furthermore, it assumes that there are no delimiters ending '/' (which is the current situation). Keep this in mind for
    // future changes.
    return `${url.pathname.substring(0, htmlEndIndex + delimiter.length)}/`;
  }

  // If we don't find the AEM pathname, we assume that the current pathname
  // is the main namespace pathname and the basePathname is empty.
  return '/';
}

/**
 * Compares two <string, string> objects to see if they contain the same keys and values.
 * Two `null`-ish objects are considered always equal; a `null`-ish and non-`null`-ish are
 * always different.
 *
 * @private
 * @ignore
 * @param {(Record<string, string> | null)} [first] The first object to compare
 * @param {(Record<string, string> | null)} [second] The second object to compare
 * @return {*} {boolean} `true` if they are equal, `false` if they are not
 */
export function areRecordsEqual(
  first?: Record<string, string> | null,
  second?: Record<string, string> | null,
): boolean {
  if (first == null && second == null) {
    return true;
  }
  if (first == null || second == null) {
    return false;
  }

  const firstKeys = Object.keys(first);
  const secondKeys = Object.keys(second);

  if (firstKeys.length !== secondKeys.length) {
    return false;
  }

  return firstKeys.every((firstKey) => first[firstKey] === second[firstKey]);
}

/**
 * Extracts and returns a plain javascript object with the key value pairs for all search parameters
 * of the given `namespace`. The values are URL-decoded.
 *
 * If no `namespace` is provided all the present parameters will be returned unfiltered.
 *
 * If the `namespace` is not the main namespace, the subpath parameter will NOT be included in the
 * result, since it isn't considered part of the search parameters of the namespace.
 *
 * @private
 * @ignore
 * @param {URL} url The URL from which to extract the search parameters
 * @param {(string | null)} [namespace] The namesapce for which to extract the search parameters
 * @return {*} {Record<string, string>} The resulting key-value record
 */
export function extractSearchParameters(
  url: URL,
  namespace?: string | null,
  mainNamespace?: string | null,
): Record<string, string> {
  if (namespace == null) {
    return composeRecordFromSearchParams(url.searchParams);
  }

  const removeNamespace = (key: string) => key.substr(namespace.length);
  if (namespace === mainNamespace) {
    return composeRecordFromSearchParams(url.searchParams, (key: string) => key.startsWith(namespace), removeNamespace);
  }

  const subPathnameKey = `${namespace}Path`;
  return composeRecordFromSearchParams(
    url.searchParams,
    (key: string) => key.startsWith(namespace) && key != subPathnameKey,
    removeNamespace,
  );
}

/**
 * Tries to decode the parameter using `decodeURIComponent`
 *
 * If it is not possible to `decodeURIComponent` returns the initial parameter value.
 * @param parameter
 * @returns parameter or decodeURIComponent parameter
 */
export const decodeParameter = (parameter: string): string => {
  try {
    return decodeURIComponent(parameter);
  } catch {
    // returning initial parameter
    return parameter;
  }
};

/**
 * Walks through all of provided `parameters` of a URL, and returns the URL-decoded values in
 * a plain javascript object.
 *
 * If a `filter` function is provided, it will be used to know which records to add onto the
 * resulting object.
 *
 * If a `transformKey` function is provided, it will be used to transfrom keys before adding
 * them to the resulting object. This is mainly used to remove the namespace from namespaced
 * parameters, interpreting `namespacedKey=1` as `key=1`.
 *
 * @private
 * @ignore
 * @param {URLSearchParams} parameters The search parameters to walk through and filter
 * @param {(((key: string, value: string) => boolean) | null)} [filter] The filter function to apply
 * @param {(((key: string) => string) | null)} [transformKey] The transform function to apply to keys
 * @return {*} {Record<string, string>} The resulting javascript object
 */
export function composeRecordFromSearchParams(
  parameters: URLSearchParams,
  filter?: ((key: string, value: string) => boolean) | null,
  transformKey?: ((key: string) => string) | null,
): Record<string, string> {
  const record: Record<string, string> = {};
  for (const [key, value] of parameters) {
    const decodedValue = decodeParameter(value);
    if (!filter || filter(key, decodedValue)) {
      const actualKey = transformKey ? transformKey(key) : key;
      record[actualKey] = decodedValue;
    }
  }
  return record;
}

/**
 * Converts a match string given a namespace, it will extract from the string the
 * pathname, searchParams and hash values and convert them into a URLInitOptions object
 *
 * @param {string} namespace search params with the given namespace
 * @param {string} matchString string that we want to convert
 * @returns {URLInitOptions} options generated from the given matchString
 */
export function convertMatchStringToURLOptions(namespace: string, matchString: string): URLInitOptions {
  const option: URLInitOptions = {
    namespace,
    pathname: '',
  };

  if (matchString == null || matchString.length === 0) {
    return option;
  }

  if (matchString === '*') {
    option.pathname = '*';
  }

  const searchIndex = matchString.indexOf('?');
  const hashIndex = matchString.indexOf('#');

  if (matchString.startsWith('/')) {
    let pathnameQuery = matchString;
    if (searchIndex >= 0 || hashIndex >= 0) {
      const targetIndex = searchIndex >= 0 ? searchIndex : hashIndex;
      pathnameQuery = pathnameQuery.substring(0, targetIndex);
    }
    option.pathname = pathnameQuery;
  }

  if (searchIndex >= 0) {
    const searchStringEndIndex = hashIndex >= 0 ? hashIndex : matchString.length;
    const searchString = matchString.substring(searchIndex + 1, searchStringEndIndex);
    const requiredMatches = searchString.split('&').map((parameter) => parameter.split('='));
    requiredMatches.forEach(([key, value]) => {
      if (!BLACKLISTED_SEARCH_PARAMETERS.includes(key)) {
        if (option.searchParams === undefined || option.searchParams == null) {
          option.searchParams = {};
        }
        option.searchParams[key] = value;
      }
    });
  }

  if (hashIndex >= 0) {
    option.hash = matchString.substr(hashIndex + 1);
  }
  return option;
}

/**
 * Util that checks if the current environment is a browser
 * useful to avoid using window object in SSR
 */
export const isBrowser = () => typeof window !== 'undefined';

/**
 * Register the CustomElementConstructor in the browser's `customElements` registry with the given `tagName`.
 *
 * @export
 * @param {string} [tagName='sw-router-link-target'] Example: The router link target tag name
 * @return {*}  {void}
 */
export function defineElement(CustomElementConstructor: CustomElementConstructor, tagName: string): void {
  if (!tagName) {
    console.error('tagName is required');
    return;
  }

  if (window.customElements.get(tagName) != null) {
    return;
  }

  window.customElements.define(tagName, CustomElementConstructor);
  console.info(`[${tagName}] defined element as <${tagName}>`);
}
